/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 16 2023
 **/

import React, { useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import {
	getChatTitleByUserList,
	sortDMList,
	useChatMessageActions,
	useChatRoomActions,
	useDMRoomListState
} from "@/frameworks/core/chat";
import { GROUP_POPOVER_ID, ModalID, SidebarID, ZIndex } from "@/frameworks/core/commons";
import { InviteChatMember, PanelRoomItem, ShowChatMember, GuestCheck } from "@/frameworks/core/components";
import { usePopover, useSidebarActions } from "@/frameworks/core/hooks";
import { Button, Icon, Row, Typography, useFoldingB, useModal } from "@/frameworks/designStuff/components";
import { Each, Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useChatDataStore } from "@/modules/chat/store";
import { Properties } from "@/frameworks/libraries";

import { EmptyDMChatList, FirstFoldingContent, SecondFoldingContent } from ".";
import { DMChatOptionList } from "../../DMChatOptionList";
import * as S from "../emotion";

type DirectChatListProps = {
	currentChatSelected?: string;
	handleSelectChat: (channel: string) => void;
};

export default React.memo(function DirectChatList({ currentChatSelected, handleSelectChat }: DirectChatListProps) {
	const modal = useModal();
	const popover = usePopover();
	const sidebarActions = useSidebarActions();
	const { chats } = usePersistActions();

	const DMSubMenuAnchorRef = useRef<Record<string, HTMLLIElement | null>>({});
	const foldingB = useFoldingB();
	const chatRoomActions = useChatRoomActions();
	const { prioritySpaceChat, showFullSizeChat } = useChatDataStore(
		useShallow((state) => ({
			prioritySpaceChat: state.priority_space_chat,
			showFullSizeChat: state.show_fullsize_chat
		}))
	);
	const dmRoomList = useDMRoomListState();
	const { messageReaded } = useChatMessageActions();

	const isGuest = Properties.property.isGuest;

	const isEmptyDMChatList = useMemo(
		() =>
			dmRoomList.filter((channel) => {
				const DMChannel = channel as WebServices.DirectMessage.List.Data[number];
				return !DMChannel.deleted && !DMChannel.hide;
			}).length === 0,
		[dmRoomList]
	);

	const handleClick = (evt: React.MouseEvent<HTMLElement>) => {
		evt.stopPropagation();
		const { sidebar, modal: _modal, channel = "", newMessage, messageId = "" } = evt.currentTarget.dataset;
		if (channel) {
			showFullSizeChat && useChatDataStore.getState().clearThreadID();
			chatRoomActions.common.open({ channelID: channel, type: "DM" });
			handleSelectChat(channel);
			if (newMessage) messageReaded({ channelID: channel, messageID: messageId });
		}

		if (_modal) {
			switch (_modal) {
				case "invite-chat-member":
					{
						modal.show({
							id: ModalID.INVITE_CHAT_MEMBER,
							content: (
								<InviteChatMember
									chatKind="DM"
									onHide={() => {
										modal.hide(ModalID.INVITE_CHAT_MEMBER);
									}}
								/>
							)
						});
					}
					break;

				case "show-chat-member": {
					modal.show({
						id: ModalID.SHOW_CHAT_MEMBER,
						content: (
							<ShowChatMember
								onHide={() => modal.hide(ModalID.SHOW_CHAT_MEMBER)}
								channelID={channel}
								chatKind="DM"
							/>
						)
					});
				}
			}
		}

		if (sidebar) {
			switch (sidebar) {
				default: {
					sidebarActions.show(sidebar as SidebarID);
					break;
				}
			}
		}
	};

	const handleMoreClick = (evt: React.MouseEvent<HTMLButtonElement>, idx: string) => {
		evt.stopPropagation();

		const { channel = "" } = evt.currentTarget.dataset;
		popover.show({
			popoverID: GROUP_POPOVER_ID.CHAT_OPTION,
			uniqueID: "option",
			content: (
				<div css={{ transform: "translateX(-50%)" }}>
					<DMChatOptionList channelID={channel} />
				</div>
			),
			anchorEl: DMSubMenuAnchorRef.current?.[idx],
			direction: {
				x: "right",
				y: "bottom"
			},
			position: {
				x: 80,
				y: -40
			},
			zIndex: ZIndex.POPUP
		});
	};

	const FoldingBContents = useMemo(() => {
		if (prioritySpaceChat) return SecondFoldingContent;
		return FirstFoldingContent;
	}, [prioritySpaceChat]);

	const sortedDMList = useMemo(() => [...dmRoomList].sort(sortDMList), [dmRoomList]);
	const myID = Configure.userID;

	return (
		<FoldingBContents
			id="dm-chat"
			FoldingHeaderContent={
				<Maybe test={!isEmptyDMChatList}>
					<S.DrawerContentHeader verticalAlign="center" horizonAlign="distribute">
						<Row verticalAlign="center">
							<Maybe
								test={foldingB.state["dm-chat"]?.isOpen}
								fallback={
									<Icon
										name="RightFilled16"
										size={20}
										onClick={() => foldingB.handlers.handleToggle("dm-chat")}
									/>
								}
							>
								<Icon
									name="DownFilled16"
									size={20}
									onClick={() => foldingB.handlers.handleToggle("dm-chat")}
								/>
							</Maybe>
							<Typography typoSize="Title3" css={{ paddingLeft: "4px" }}>
								{_T("다이렉트 채팅")}
							</Typography>
						</Row>
						<S.HeaderIconList>
							<Button.Icon
								name="Add24"
								size="large"
								variant="text"
								onClick={handleClick}
								data-modal="invite-chat-member"
							/>
						</S.HeaderIconList>
					</S.DrawerContentHeader>
				</Maybe>
			}
			FoldingBodyContent={
				<GuestCheck
					fallbackType="signupPanel"
					panelType="CHAT"
					condition={!isGuest || (isGuest && !isEmptyDMChatList)}
				>
					<Maybe test={!isEmptyDMChatList} fallback={<EmptyDMChatList />}>
						<S.DirectMessageContent>
							<S.ShortDirectMessageList>
								<Each
									useArray
									of={sortedDMList}
									render={(channel, idx) => {
										const DMChannel = channel as WebServices.DirectMessage.List.Data[number];
										const _badgeList = [] as ("new" | "pin")[];
										if (DMChannel.pinned) _badgeList.push("pin");
										if (DMChannel.hasNewMessage) _badgeList.push("new");
										if (DMChannel.deleted || DMChannel.hide) return null;
										const hasDraft = chats.has_draft(channel.roomId);
										return (
											<li
												key={idx}
												ref={(element) => (DMSubMenuAnchorRef.current[`${idx}`] = element)}
												style={{ cursor: "pointer" }}
											>
												<PanelRoomItem
													id={"panelRoom-" + `${channel.roomId}`}
													roomID={channel.roomId}
													userIDList={
														DMChannel.users.length === 1 &&
														DMChannel.users.some((user) => user?.id === myID)
															? DMChannel.users.map((user) => user.id)
															: DMChannel.users
																	.filter((user) => user.id !== myID)
																	.map((user) => user.id)
													}
													title={getChatTitleByUserList(
														DMChannel.users,
														DMChannel.users.length
													)}
													desc={``}
													badgeList={_badgeList}
													hasDraft={hasDraft}
													onClick={handleClick}
													isVoiceCall={DMChannel.inVoiceChat}
													onMoreClick={(evt: React.MouseEvent<HTMLButtonElement>) =>
														handleMoreClick(evt, `${idx}`)
													}
													data-new-message={DMChannel.hasNewMessage}
													data-message-id={DMChannel.lastSentMessage}
													data-sidebar={showFullSizeChat ? SidebarID.FullSizeChat : ""}
													data-channel={DMChannel.roomId}
													data-name={DMChannel.users
														.reduce((acc: string[], o) => {
															o.name && acc.push(o.name);
															return acc;
														}, [])
														.join(", ")}
													isChatFeature
													activeBackground={currentChatSelected === channel.roomId}
												/>
											</li>
										);
									}}
								/>
							</S.ShortDirectMessageList>
						</S.DirectMessageContent>
					</Maybe>
				</GuestCheck>
			}
		/>
	);
});
